/**
 * Service API pour les fonctionnalités de recommandations personnalisées (Module Aiguille)
 */

import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3002/api';

// Configuration axios avec authentification
const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Intercepteur pour ajouter le token à chaque requête
api.interceptors.request.use(config => {
  const authHeader = getAuthHeader();
  config.headers = { ...config.headers, ...authHeader };
  return config;
});

// ==================== 1. RECOMMANDATIONS PERSONNALISÉES ====================

/**
 * Génère des recommandations personnalisées pour un utilisateur
 * @param {number} userId - ID de l'utilisateur (optionnel)
 * @param {Array} competences - Liste des compétences (optionnel)
 * @param {string} level - Niveau de qualification (optionnel)
 * @param {string} sector - Secteur d'activité (optionnel)
 * @param {number} limit - Nombre de recommandations (défaut: 20)
 */
export const getPersonalizedRecommendations = async (userId = null, competences = null, level = null, sector = null, limit = 20) => {
  const response = await api.post('/recommendations/personalized', {
    userId,
    competences,
    level,
    sector,
    limit
  });
  return response.data;
};

// ==================== 2. MATCHING INTELLIGENT ====================

/**
 * Effectue un matching intelligent entre profil et cibles
 * @param {string} sourceType - Type de source (user, competence_profile)
 * @param {number} sourceId - ID de la source (optionnel)
 * @param {Array} sourceCompetences - Compétences source (optionnel)
 * @param {string} targetType - Type de cible (job, formation, certification)
 * @param {Object} filters - Filtres supplémentaires
 */
export const intelligentMatch = async (sourceType, sourceId = null, sourceCompetences = null, targetType, filters = {}) => {
  const response = await api.post('/recommendations/match', {
    sourceType,
    sourceId,
    sourceCompetences,
    targetType,
    filters
  });
  return response.data;
};

/**
 * Shortcut: Match utilisateur vers formations
 */
export const matchUserToFormations = async (userId, filters = {}) => {
  return intelligentMatch('user', userId, null, 'formation', filters);
};

/**
 * Shortcut: Match utilisateur vers certifications
 */
export const matchUserToCertifications = async (userId, filters = {}) => {
  return intelligentMatch('user', userId, null, 'certification', filters);
};

/**
 * Shortcut: Match compétences vers jobs
 */
export const matchCompetencesToJobs = async (competences, filters = {}) => {
  return intelligentMatch('competence_profile', null, competences, 'job', filters);
};

// ==================== 3. PROFIL DYNAMIQUE ====================

/**
 * Récupère le profil dynamique d'un utilisateur
 * @param {number} userId - ID de l'utilisateur
 */
export const getDynamicProfile = async (userId) => {
  const response = await api.get(`/recommendations/profile/${userId}`);
  return response.data;
};

/**
 * Met à jour le profil dynamique après une action
 * @param {number} userId - ID de l'utilisateur
 * @param {Array} competencesToAdd - Compétences à ajouter
 * @param {Array} competencesToRemove - Compétences à retirer
 * @param {number} projectId - ID du projet concerné (optionnel)
 */
export const updateDynamicProfile = async (userId, competencesToAdd = [], competencesToRemove = [], projectId = null) => {
  const response = await api.post(`/recommendations/profile/${userId}/update`, {
    competencesToAdd,
    competencesToRemove,
    projectId
  });
  return response.data;
};

// ==================== 4. MOTEUR DE SIMILARITÉ ====================

/**
 * Calcule la similarité entre deux entités
 * @param {Object} entity1 - Première entité {text, description}
 * @param {Object} entity2 - Deuxième entité {text, description}
 * @param {string} method - Méthode de calcul (cosine, jaccard, levenshtein)
 */
export const calculateSimilarity = async (entity1, entity2, method = 'cosine') => {
  const response = await api.post('/recommendations/similarity', {
    entity1,
    entity2,
    method
  });
  return response.data;
};

// ==================== 5. SCORING DE PERTINENCE ====================

/**
 * Calcule un score de pertinence entre profil actuel et objectif
 * @param {Object} currentProfile - Profil actuel {competences, level, sector, experience}
 * @param {Object} targetProfile - Profil cible {competences, level, sector}
 * @param {Object} weights - Poids personnalisés {competences, level, sector, experience}
 */
export const calculateRelevanceScore = async (currentProfile, targetProfile, weights = null) => {
  const response = await api.post('/recommendations/relevance-score', {
    currentProfile,
    targetProfile,
    weights
  });
  return response.data;
};

/**
 * Évalue l'adéquation utilisateur-formation
 */
export const evaluateUserFormationFit = async (userId, formationId) => {
  // Récupérer le profil utilisateur
  const userProfile = await getDynamicProfile(userId);

  // Simuler le profil de la formation (à remplacer par une vraie requête)
  const targetProfile = {
    competences: 'formation specific competences',
    level: 'niveau_5',
    sector: 'informatique'
  };

  const currentProfile = {
    competences: userProfile.profile.map(c => c.label).join(' '),
    level: userProfile.level,
    sector: userProfile.sector
  };

  return calculateRelevanceScore(currentProfile, targetProfile);
};

// ==================== 6. FILTRES AVANCÉS ====================

/**
 * Applique des filtres avancés sur les entités
 * @param {string} entityType - Type d'entité (formations, certifications, jobs)
 * @param {string} geography - Localisation géographique
 * @param {string} sector - Secteur d'activité
 * @param {string} accessibility - Mode d'accès (online, presence, hybrid, all)
 * @param {string} level - Niveau de qualification
 * @param {string} duration - Durée (optionnel)
 * @param {string} cost - Coût (optionnel)
 */
export const applyAdvancedFilters = async (
  entityType,
  geography = null,
  sector = null,
  accessibility = 'all',
  level = null,
  duration = null,
  cost = null
) => {
  const params = {
    entityType,
    ...(geography && { geography }),
    ...(sector && { sector }),
    accessibility,
    ...(level && { level }),
    ...(duration && { duration }),
    ...(cost && { cost })
  };

  const response = await api.get('/recommendations/advanced-filters', { params });
  return response.data;
};

/**
 * Filtres prédéfinis pour formations en ligne
 */
export const getOnlineFormations = async (sector = null, level = null) => {
  return applyAdvancedFilters('formations', null, sector, 'online', level);
};

/**
 * Filtres prédéfinis pour formations en présentiel
 */
export const getInPersonFormations = async (geography, sector = null, level = null) => {
  return applyAdvancedFilters('formations', geography, sector, 'presence', level);
};

// ==================== 7. PASSERELLES MÉTIERS ====================

/**
 * Suggère des passerelles métiers pour la mobilité professionnelle
 * @param {string} currentJob - Métier actuel
 * @param {string} currentSector - Secteur actuel
 * @param {number} userId - ID utilisateur (optionnel)
 */
export const getCareerBridges = async (currentJob = null, currentSector = null, userId = null) => {
  const params = {
    ...(currentJob && { currentJob }),
    ...(currentSector && { currentSector }),
    ...(userId && { userId })
  };

  const response = await api.get('/recommendations/career-bridges', { params });
  return response.data;
};

/**
 * Récupère les passerelles pour un utilisateur spécifique
 */
export const getUserCareerBridges = async (userId) => {
  return getCareerBridges(null, null, userId);
};

/**
 * Filtrer les passerelles par difficulté
 */
export const filterBridgesByDifficulty = (bridges, difficulty) => {
  return bridges.filter(b => b.difficulty === difficulty);
};

// ==================== 8. IA EXPLICATIVE ====================

/**
 * Demande une explication pour une recommandation
 * @param {string} recommendationId - ID de la recommandation
 * @param {string} recommendationType - Type (formation, certification, job, competence)
 * @param {Object} userProfile - Profil utilisateur
 * @param {number} score - Score de la recommandation (optionnel)
 */
export const explainRecommendation = async (recommendationId, recommendationType, userProfile, score = null) => {
  const response = await api.post('/recommendations/explain', {
    recommendationId,
    recommendationType,
    userProfile,
    ...(score && { score })
  });
  return response.data;
};

/**
 * Obtenir une explication détaillée pour une formation recommandée
 */
export const explainFormationRecommendation = async (formationId, userProfile, score) => {
  return explainRecommendation(formationId, 'formation', userProfile, score);
};

/**
 * Obtenir une explication détaillée pour une certification recommandée
 */
export const explainCertificationRecommendation = async (certificationId, userProfile, score) => {
  return explainRecommendation(certificationId, 'certification', userProfile, score);
};

// ==================== UTILITAIRES ====================

/**
 * Formate un score en pourcentage
 */
export const formatScore = (score) => {
  return `${Math.round(score * 100)}%`;
};

/**
 * Classe une recommandation par niveau de pertinence
 */
export const classifyRecommendation = (score) => {
  if (score > 0.8) return { level: 'excellent', color: 'green', label: 'Excellent match' };
  if (score > 0.6) return { level: 'good', color: 'blue', label: 'Bon match' };
  if (score > 0.4) return { level: 'moderate', color: 'orange', label: 'Match modéré' };
  return { level: 'low', color: 'red', label: 'Match faible' };
};

/**
 * Trie les recommandations par score décroissant
 */
export const sortRecommendationsByScore = (recommendations) => {
  return [...recommendations].sort((a, b) => b.score - a.score);
};

export default {
  getPersonalizedRecommendations,
  intelligentMatch,
  matchUserToFormations,
  matchUserToCertifications,
  matchCompetencesToJobs,
  getDynamicProfile,
  updateDynamicProfile,
  calculateSimilarity,
  calculateRelevanceScore,
  evaluateUserFormationFit,
  applyAdvancedFilters,
  getOnlineFormations,
  getInPersonFormations,
  getCareerBridges,
  getUserCareerBridges,
  filterBridgesByDifficulty,
  explainRecommendation,
  explainFormationRecommendation,
  explainCertificationRecommendation,
  formatScore,
  classifyRecommendation,
  sortRecommendationsByScore
};
